/**
 * Auto-Match Dialog: Generic, direction-agnostic field matching preview.
 *
 * Reused by both Input and Output mapping tabs. The dialog is purely
 * presentational — it computes suggestions via `autoMatchFields` and
 * returns the selected `AutoMatchSuggestion[]` to the caller. Each tab
 * converts suggestions to its own mapping type (SOLID: single responsibility).
 *
 * Each suggestion row has a dropdown on the source side so the user can
 * switch to an alternative match when multiple candidates exist.
 */

import {
  Button,
  Checkbox,
  Menu,
  MenuItem,
  Modal,
  notify,
} from '@affine/component';
import { useI18n } from '@affine/i18n';
import { checkCompatibility, normalizeType } from '@streamlain/field-types';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type {
  AutoMatchSuggestion,
  ResponseFieldInfo,
  TargetColumnInfo,
} from '../utils/auto-match-fields';
import { autoMatchFields } from '../utils/auto-match-fields';
import { CompatDot, TypeChip } from './type-chip';

// =============================================================================
// Props
// =============================================================================

interface AutoMatchDialogProps {
  open: boolean;
  onClose: () => void;
  sourceFields: ResponseFieldInfo[];
  targetFields: TargetColumnInfo[];
  existingTargetIds?: Set<string>;
  sourceLabel?: string;
  targetLabel?: string;
  onApply: (suggestions: AutoMatchSuggestion[]) => void;
}

// =============================================================================
// Constants
// =============================================================================

const CONFIDENCE_COLORS: Record<string, string> = {
  high: '#4CAF50',
  medium: '#FF9800',
  low: '#9ca3af',
};

// Labels resolved via i18n at render time — see ConfidenceBadge
const CONFIDENCE_KEYS: Record<string, string> = {
  high: 'com.streamlain.streamlain.automap.confidence-high',
  medium: 'com.streamlain.streamlain.automap.confidence-medium',
  low: 'com.streamlain.streamlain.automap.confidence-low',
};

/**
 * Generate a specific tooltip for a source→target type cast.
 */
function castTooltip(sourceType: string, targetType: string): string {
  const s = normalizeType(sourceType);
  const t = normalizeType(targetType);
  if (s === t)
    return `${sourceType} → ${targetType}: Compatible — no conversion needed`;

  // Specific cast descriptions
  if (s === 'select' && t === 'text')
    return 'Select → Text: Option label as string';
  if (s === 'multi-select' && t === 'text')
    return 'Multi-Select → Text: Labels joined with comma';
  if (s === 'number' && t === 'text') return 'Number → Text: Numeric string';
  if (s === 'date' && t === 'text') return 'Date → Text: ISO date string';
  if (s === 'checkbox' && t === 'text')
    return 'Checkbox → Text: "true" or "false"';
  if (s === 'rich-text' && t === 'text')
    return '⚠ Rich-Text → Text: Markup/formatting is stripped';
  if (s === 'attachment' && t === 'text')
    return '⚠ Attachment → Text: Only filename — file data is lost';
  if (s === 'relation' && t === 'text')
    return '⚠ Relation → Text: Outputs relation ID. Can reconstruct link in a target relation column if configured correctly — otherwise renders as raw ID';
  if (s === 'text' && t === 'relation')
    return '⚠ Text → Relation: High risk — only works if the value is a valid relation ID and the target column accepts it';
  if (s === 'text' && t === 'rich-text')
    return 'Text → Rich-Text: Plain text wrapped in paragraph';
  if (
    (s === 'select' && t === 'multi-select') ||
    (s === 'multi-select' && t === 'select')
  )
    return `${sourceType} ↔ ${targetType}: Semantically related — single/multi conversion`;
  if (t === 'json') return `${sourceType} → JSON: Value serialized as JSON`;
  if (s === 'json') return `JSON → ${targetType}: Value extracted from JSON`;

  const compat = checkCompatibility(targetType, sourceType);
  if (compat === 'castable')
    return `${sourceType} → ${targetType}: Auto-cast — may lose precision`;
  return `${sourceType} → ${targetType}: Types may not be compatible`;
}

/** Shared confidence badge — DRY across main row and dropdown. */
function ConfidenceBadge({
  confidence,
  t,
}: {
  confidence: string;
  t: ReturnType<typeof useI18n>;
}) {
  const key = CONFIDENCE_KEYS[confidence];
  const label = key
    ? ((t[key as keyof typeof t] as (() => string) | undefined)?.() ??
      confidence)
    : confidence;
  return (
    <span
      style={{
        fontSize: '10px',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: '10px',
        backgroundColor: CONFIDENCE_COLORS[confidence] ?? '#9ca3af',
        color: 'white',
        minWidth: '52px',
        textAlign: 'center',
        display: 'inline-block',
        flexShrink: 0,
        lineHeight: '16px',
        boxSizing: 'border-box',
      }}
    >
      {label}
    </span>
  );
}

// =============================================================================
// Component
// =============================================================================

export const AutoMatchDialog: FC<AutoMatchDialogProps> = ({
  open,
  onClose,
  sourceFields,
  targetFields,
  existingTargetIds,
  sourceLabel = 'Source',
  targetLabel = 'Target',
  onApply,
}) => {
  const t = useI18n();

  const initialSuggestions = useMemo(() => {
    return autoMatchFields(
      sourceFields,
      targetFields,
      existingTargetIds ?? new Set()
    );
  }, [sourceFields, targetFields, existingTargetIds]);

  // Mutable suggestions state — user can switch alternatives via dropdown
  const [suggestions, setSuggestions] =
    useState<AutoMatchSuggestion[]>(initialSuggestions);

  const [enabled, setEnabled] = useState<Set<string>>(
    () => new Set(initialSuggestions.map(s => s.targetColumnId))
  );

  // Reset when initial suggestions change (dialog reopened)
  useMemo(() => {
    setSuggestions(initialSuggestions);
    setEnabled(new Set(initialSuggestions.map(s => s.targetColumnId)));
  }, [initialSuggestions]);

  const toggleSuggestion = useCallback((targetId: string) => {
    setEnabled(prev => {
      const next = new Set(prev);
      if (next.has(targetId)) next.delete(targetId);
      else next.add(targetId);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    if (enabled.size === suggestions.length) {
      setEnabled(new Set());
    } else {
      setEnabled(new Set(suggestions.map(s => s.targetColumnId)));
    }
  }, [enabled.size, suggestions]);

  const enabledCount = useMemo(
    () => suggestions.filter(s => enabled.has(s.targetColumnId)).length,
    [suggestions, enabled]
  );

  /** Switch the selected source for a target to a different alternative. */
  const switchAlternative = useCallback(
    (targetId: string, newResponseField: string) => {
      setSuggestions(prev =>
        prev.map(s => {
          if (s.targetColumnId !== targetId) return s;
          const alt = s.alternatives.find(
            a => a.responseField === newResponseField
          );
          if (!alt) return s;
          return {
            ...s,
            responseField: alt.responseField,
            responseFieldName: alt.responseFieldName,
            confidence: alt.confidence,
          };
        })
      );
    },
    []
  );

  const handleApply = useCallback(() => {
    const selected = suggestions.filter(s => enabled.has(s.targetColumnId));
    onApply(selected);
    notify.success({
      title: t['com.streamlain.streamlain.automap.success']({
        count: String(selected.length),
      }),
    });
    onClose();
  }, [suggestions, enabled, onApply, onClose, t]);

  // Type lookup
  const sourceTypeMap = useMemo(
    () => new Map(sourceFields.map(f => [f.path, f.type])),
    [sourceFields]
  );
  const targetTypeMap = useMemo(
    () => new Map(targetFields.map(c => [c.id, c.type])),
    [targetFields]
  );

  const unmatchedTargets = useMemo(() => {
    const matchedIds = new Set(suggestions.map(s => s.targetColumnId));
    return targetFields.filter(c => !matchedIds.has(c.id));
  }, [suggestions, targetFields]);

  return (
    <Modal
      open={open}
      onOpenChange={v => {
        if (!v) onClose();
      }}
      title={t['com.streamlain.streamlain.automap.title']()}
      width={560}
    >
      <div
        style={{
          padding: '0 24px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {/* Explanation */}
        <div
          style={{
            padding: '12px',
            fontSize: '13px',
            lineHeight: 1.5,
            backgroundColor: 'var(--streamlain-background-secondary-color)',
            borderRadius: '8px',
          }}
        >
          {t['com.streamlain.streamlain.automap.description']()}
        </div>

        {suggestions.length === 0 ? (
          <div
            style={{
              padding: '24px',
              textAlign: 'center',
              fontSize: '13px',
              opacity: 0.6,
            }}
          >
            {t['com.streamlain.streamlain.automap.no-matches']()}
          </div>
        ) : (
          <>
            {/* Header — same 7-col grid as rows:
                checkbox | src-name | src-pill | src-arrow | tgt-name | tgt-pill | match */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '28px 1fr auto 16px 1fr auto auto',
                gap: '8px',
                padding: '4px 0',
                fontSize: '11px',
                fontWeight: 600,
                borderBottom: '1px solid var(--streamlain-border-color)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontSize: '14px',
                }}
              >
                <Checkbox
                  checked={enabled.size === suggestions.length}
                  onChange={() => toggleAll()}
                />
              </div>
              <span style={{ opacity: 0.5, gridColumn: 'span 3' }}>
                {sourceLabel}
              </span>
              <span style={{ opacity: 0.5, gridColumn: 'span 2' }}>
                {targetLabel}
              </span>
              <span style={{ opacity: 0.5, textAlign: 'right' }}>
                {t['com.streamlain.streamlain.automap.match']()}
              </span>
            </div>

            {/* Suggestion rows */}
            <div
              style={{
                maxHeight: '320px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              {suggestions.map(s => {
                const hasAlternatives = s.alternatives.length > 1;
                const sourceType = sourceTypeMap.get(s.responseField) ?? 'text';
                const targetType =
                  targetTypeMap.get(s.targetColumnId) ?? 'text';

                // DRY: dropdown-item label (Name + TypeChip) for the Menu
                // popover rows. The main row uses explicit grid cells below.
                const renderMenuItemLabel = (
                  name: string | undefined,
                  path: string,
                  type: string
                ) => (
                  <>
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {name || path}
                    </span>
                    <TypeChip type={type} />
                  </>
                );

                return (
                  <div
                    key={s.targetColumnId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '28px 1fr auto 16px 1fr auto auto',
                      gap: '8px',
                      alignItems: 'center',
                      padding: '6px 0',
                      fontSize: '12px',
                      opacity: enabled.has(s.targetColumnId) ? 1 : 0.4,
                    }}
                  >
                    {/* Checkbox */}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <Checkbox
                        checked={enabled.has(s.targetColumnId)}
                        onChange={() => toggleSuggestion(s.targetColumnId)}
                      />
                    </div>

                    {/* Source name */}
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.responseFieldName || s.responseField}
                    </span>

                    {/* Source pill */}
                    <TypeChip type={sourceType} />

                    {/* Source alternatives dropdown — own cell, right of pill */}
                    {hasAlternatives ? (
                      <Menu
                        items={s.alternatives.map(alt => (
                          <MenuItem
                            key={alt.responseField}
                            onClick={() =>
                              switchAlternative(
                                s.targetColumnId,
                                alt.responseField
                              )
                            }
                          >
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto auto auto',
                                gap: '8px',
                                alignItems: 'center',
                                width: '100%',
                                padding: '2px 4px',
                                borderRadius: '4px',
                                backgroundColor:
                                  alt.responseField === s.responseField
                                    ? 'color-mix(in srgb, var(--streamlain-primary-color) 12%, transparent)'
                                    : 'transparent',
                              }}
                            >
                              {renderMenuItemLabel(
                                alt.responseFieldName,
                                alt.responseField,
                                sourceTypeMap.get(alt.responseField) ?? 'text'
                              )}
                              <ConfidenceBadge
                                confidence={alt.confidence}
                                t={t}
                              />
                              <CompatDot
                                compatibility={checkCompatibility(
                                  targetType,
                                  sourceTypeMap.get(alt.responseField) ?? 'text'
                                )}
                                tooltip={castTooltip(
                                  sourceTypeMap.get(alt.responseField) ??
                                    'text',
                                  targetType
                                )}
                              />
                            </div>
                          </MenuItem>
                        ))}
                        contentOptions={{
                          side: 'bottom',
                          align: 'start',
                          sideOffset: 4,
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            width: 16,
                            height: 16,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            margin: 0,
                            fontSize: '10px',
                            opacity: 0.5,
                            cursor: 'pointer',
                            lineHeight: 1,
                          }}
                        >
                          ▼
                        </button>
                      </Menu>
                    ) : (
                      <div style={{ width: 16, height: 16 }} />
                    )}

                    {/* Target name */}
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.targetColumnName}
                    </span>

                    {/* Target pill — own cell */}
                    <TypeChip type={targetType} />

                    {/* Match: confidence badge + compat dot */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <ConfidenceBadge confidence={s.confidence} t={t} />
                      <CompatDot
                        compatibility={checkCompatibility(
                          targetType,
                          sourceType
                        )}
                        tooltip={castTooltip(sourceType, targetType)}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Unmatched info */}
            {unmatchedTargets.length > 0 && (
              <div style={{ fontSize: '11px', opacity: 0.5, padding: '4px 0' }}>
                {t[
                  unmatchedTargets.length === 1
                    ? 'com.streamlain.streamlain.automap.unmatched'
                    : 'com.streamlain.streamlain.automap.unmatched_plural'
                ]({ count: String(unmatchedTargets.length) })}{' '}
                {unmatchedTargets
                  .slice(0, 5)
                  .map(c => c.name)
                  .join(', ')}
                {unmatchedTargets.length > 5 &&
                  `, +${unmatchedTargets.length - 5} more`}
              </div>
            )}
          </>
        )}

        {/* Actions */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px',
            paddingTop: '8px',
            borderTop: '1px solid var(--streamlain-border-color)',
          }}
        >
          <Button onClick={onClose}>
            {t['com.streamlain.streamlain.automap.cancel']()}
          </Button>
          <Button
            variant="primary"
            onClick={handleApply}
            disabled={enabledCount === 0}
          >
            {t[
              enabledCount === 1
                ? 'com.streamlain.streamlain.automap.apply'
                : 'com.streamlain.streamlain.automap.apply_plural'
            ]({ count: String(enabledCount) })}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
